import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { lazy, Suspense } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { Toaster } from 'sonner'
import { PageLoader } from '@/components/ui/primitives'
import { AuthProvider } from '@/lib/auth'
import { PublicLayout } from '@/layouts/PublicLayout'
import { HomePage } from '@/features/public/HomePage'
import { NoticiaDetalhePage, NoticiasPage } from '@/features/public/NoticiasPage'
import { HorariosPage } from '@/features/public/HorariosPage'
import { EventoDetalhePage, EventosPage } from '@/features/public/EventosPage'
import { ComprovantePage, InscricaoPage } from '@/features/public/InscricaoPage'
import { ContatoPage, NaoEncontradaPage, PrivacidadePage } from '@/features/public/PublicPages'

/**
 * O painel administrativo e carregado em chunk separado.
 *
 * Quem abre uma noticia no celular em 4G nao deve baixar o editor de texto
 * rico, as telas de balancete e o PDV junto - e a maior parte do peso da
 * aplicacao, e serve a poucas pessoas.
 */
const AdminLayout = lazy(() =>
  import('@/layouts/AdminLayout').then((m) => ({ default: m.AdminLayout })),
)
const LoginPage = lazy(() =>
  import('@/features/admin/LoginPage').then((m) => ({ default: m.LoginPage })),
)
const DashboardPage = lazy(() =>
  import('@/features/admin/DashboardPage').then((m) => ({ default: m.DashboardPage })),
)
const UsuariosPage = lazy(() =>
  import('@/features/admin/UsuariosPage').then((m) => ({ default: m.UsuariosPage })),
)
const MinhaSenhaPage = lazy(() =>
  import('@/features/admin/MinhaSenhaPage').then((m) => ({ default: m.MinhaSenhaPage })),
)
const NoticiasAdminPage = lazy(() =>
  import('@/features/admin/NoticiasAdminPage').then((m) => ({ default: m.NoticiasAdminPage })),
)
const PostEditorPage = lazy(() =>
  import('@/features/admin/PostEditorPage').then((m) => ({ default: m.PostEditorPage })),
)
const TaxonomiaPage = lazy(() =>
  import('@/features/admin/TaxonomiaPage').then((m) => ({ default: m.TaxonomiaPage })),
)
const AvisosPage = lazy(() =>
  import('@/features/admin/AvisosPage').then((m) => ({ default: m.AvisosPage })),
)
const HorariosAdminPage = lazy(() =>
  import('@/features/admin/HorariosAdminPage').then((m) => ({ default: m.HorariosAdminPage })),
)
const EventosAdminPage = lazy(() =>
  import('@/features/admin/EventosAdminPage').then((m) => ({ default: m.EventosAdminPage })),
)
const EventoEditorPage = lazy(() =>
  import('@/features/admin/EventoEditorPage').then((m) => ({ default: m.EventoEditorPage })),
)
const EventoDetalheAdminPage = lazy(() =>
  import('@/features/admin/EventoDetalheAdminPage').then((m) => ({
    default: m.EventoDetalheAdminPage,
  })),
)
const PatrocinadoresPage = lazy(() =>
  import('@/features/admin/PatrocinadoresPage').then((m) => ({ default: m.PatrocinadoresPage })),
)
const PdvPage = lazy(() => import('@/features/admin/PdvPage').then((m) => ({ default: m.PdvPage })))
const ConfiguracoesPage = lazy(() =>
  import('@/features/admin/ConfiguracoesPage').then((m) => ({ default: m.ConfiguracoesPage })),
)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      // Refetch ao voltar para a aba incomoda mais do que ajuda num painel
      // administrativo, onde os dados mudam devagar.
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route element={<PublicLayout />}>
                <Route index element={<HomePage />} />

                <Route path="noticias" element={<NoticiasPage />} />
                <Route path="noticias/categoria/:categoria" element={<NoticiasPage />} />
                <Route path="noticias/assunto/:tag" element={<NoticiasPage />} />
                {/* Depois das rotas fixas: senao "categoria" cairia aqui. */}
                <Route path="noticias/:slug" element={<NoticiaDetalhePage />} />

                <Route path="horarios" element={<HorariosPage />} />

                <Route path="eventos" element={<EventosPage />} />
                <Route path="eventos/:slug" element={<EventoDetalhePage />} />
                <Route path="eventos/:slug/inscricao" element={<InscricaoPage />} />
                <Route path="comprovante/:code" element={<ComprovantePage />} />

                <Route path="contato" element={<ContatoPage />} />
                <Route path="privacidade" element={<PrivacidadePage />} />
                <Route path="*" element={<NaoEncontradaPage />} />
              </Route>

              <Route path="/entrar" element={<LoginPage />} />
              <Route path="/redefinir-senha/:token" element={<LoginPage />} />

              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<DashboardPage />} />
                <Route path="minha-senha" element={<MinhaSenhaPage />} />
                <Route path="usuarios" element={<UsuariosPage />} />

                <Route path="noticias" element={<NoticiasAdminPage />} />
                <Route path="noticias/nova" element={<PostEditorPage />} />
                <Route path="noticias/:id" element={<PostEditorPage />} />
                <Route path="taxonomia" element={<TaxonomiaPage />} />
                <Route path="avisos" element={<AvisosPage />} />
                <Route path="horarios" element={<HorariosAdminPage />} />

                <Route path="eventos" element={<EventosAdminPage />} />
                <Route path="eventos/novo" element={<EventoEditorPage />} />
                <Route path="eventos/:id/editar" element={<EventoEditorPage />} />
                <Route path="eventos/:id" element={<EventoDetalheAdminPage />} />
                <Route path="eventos/:id/pdv" element={<PdvPage />} />
                <Route path="patrocinadores" element={<PatrocinadoresPage />} />
                <Route path="configuracoes" element={<ConfiguracoesPage />} />
              </Route>
            </Routes>
          </Suspense>
          <Toaster position="top-center" richColors closeButton />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  )
}
